import React, { useState, useEffect, useMemo } from "react";
import { useFormikContext } from "formik";
import get from "lodash/get";
import Box from "@mui/material/Box";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import { orderBy, snakeCase } from "lodash";
import AddFieldModal from "../configuration/AddFieldModal";
import SearchField from "../configuration/SearchField";
import EditableBillingRates from "../configuration/EditableBillingRates";
import getBillingLabel from "../../../utils/billing/getBillingLabel";
import BillingTypeSelectionModal from "./BillingTypeSelectionModal";
import ProjectPalletTypesDialog from "./ProjectPalletTypesDialog";
import ProjectUnitTypesDialog from "./ProjectUnitTypesDialog";
import ProjectSquareFootageTypesDialog from "./ProjectSquareFootageTypesDialog";
import useAuth from "../../../hooks/useAuth";
import { getBillingTypesArray } from "../../../utils/billingHelpers";
import { getSquareFootageTypeOptions } from "../../../utils/squareFootageHelpers";
import { useProject } from "../ProjectProvider";

const styles = {
  headerCell: {
    fontWeight: "semibold",
    fontSize: 16,
    py: "8px",
  },
  cell: {
    fontSize: 16,
    py: "4px",
  },
};

export const COMMON_FIELDS = ["transaction_processing_rate"];

export const INBOUND_TRANSACTION_FIELDS = ["transaction_processing_rate"];
export const OUTBOUND_TRANSACTION_FIELDS = ["transaction_processing_rate"];

const isBillingTypeHundredweight = value => {
  if (!value) return false;
  if (value === "Hundredweight") return true;
  if (Array.isArray(value)) return value.includes("Hundredweight");
  if (typeof value === "object" && value.types) return value.types.includes("Hundredweight");
  return false;
};

const getTransactionFieldsForCategory = (category, billingTypeValue = null) => {
  switch (category) {
    case "inbound": {
      const base = INBOUND_TRANSACTION_FIELDS;
      return isBillingTypeHundredweight(billingTypeValue)
        ? [...base, "open_inspect_rate_per_cwt"]
        : base;
    }
    case "outbound":
      return OUTBOUND_TRANSACTION_FIELDS;
    default:
      return COMMON_FIELDS;
  }
};

export const BILLING_TYPE_OPTIONS = [
  "Hundredweight",
  "By the Pallet",
  "By the Pallet (Warehouse-Built)",
  "By the Unit/Item",
  "By the Vault",
  "Square Foot by Item Dimensions",
  "Square Foot by Location Dimensions",
  "Cubic Foot by Item Dimensions",
  "Cubic Foot by Location Dimensions",
  "Storage Locations Occupied",
  "Flat Rate/As Quoted",
  "Flat Rate/As Quoted (Internal Work Orders)",
  "Time and Materials (Blended Rate)",
  "Time and Materials (Itemized Rates)",
];

export default function BillingTypes({ entityPath = "", dropdownComponent, readOnly = false, focusSection = "" }) {
  const { values, setFieldValue } = useFormikContext();
  const { user } = useAuth();
  const { project } = useProject();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [showCommon, setShowCommon] = useState({});
  const [billingTypeModalOpen, setBillingTypeModalOpen] = useState(null);
  const [projectPalletTypesOpen, setProjectPalletTypesOpen] = useState(false);
  const [projectUnitTypesOpen, setProjectUnitTypesOpen] = useState(false);
  const [projectSquareFootageTypesOpen, setProjectSquareFootageTypesOpen] = useState(false);

  const activePalletTypes = useMemo(() => {
    return (project?.palletTypes || [])
      .filter(pt => pt.isActive)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }, [project?.palletTypes]);

  const palletRateKeys = useMemo(() => {
    return activePalletTypes.filter(pt => pt.id).map(pt => `${pt.id}_rate`);
  }, [activePalletTypes]);

  const activeUnitTypes = useMemo(() => {
    return (project?.unitTypes || [])
      .filter(ut => ut.isActive)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }, [project?.unitTypes]);

  const unitRateKeys = useMemo(() => {
    return activeUnitTypes.filter(ut => ut.id).map(ut => `${ut.id}_rate`);
  }, [activeUnitTypes]);

  const squareFootageRateKeys = useMemo(() => {
    return getSquareFootageTypeOptions(project?.squareFootageTypes || []).map(([id]) => `${id}_rate`);
  }, [project?.squareFootageTypes]);

  const warehouseBuiltPalletRateKeys = useMemo(() => {
    return activePalletTypes
      .filter(pt => pt.id && pt.id !== "minimum_charge")
      .map(pt => `${pt.id}_rate`);
  }, [activePalletTypes]);

  const getRateFieldsForBillingType = (
    billingType,
    category = null,
    includeTransactionFields = false
  ) => {
    const transactionFields = category ? getTransactionFieldsForCategory(category) : COMMON_FIELDS;
    const commonFields = includeTransactionFields
      ? Object.fromEntries(transactionFields.map(field => [field, ""]))
      : {};

    // Handle arrays of billing types by merging all their rate fields
    if (Array.isArray(billingType)) {
      const allFields = billingType.reduce((acc, type) => {
        const fields = getRateFieldsForBillingType(type, category, includeTransactionFields);
        return { ...acc, ...fields };
      }, {});
      return allFields;
    }

    switch (billingType) {
      case "Hundredweight":
        return {
          hundredweight_rate: "",
          minimum_charge_rate: "",
          ...commonFields,
        };
      case "By the Pallet":
        return {
          ...Object.fromEntries(palletRateKeys.map(key => [key, ""])),
          ...commonFields,
        };
      case "By the Pallet (Warehouse-Built)":
        return {
          ...Object.fromEntries(warehouseBuiltPalletRateKeys.map(key => [key, ""])),
          ...commonFields,
        };
      case "By the Unit/Item":
        return {
          ...Object.fromEntries(unitRateKeys.map(key => [key, ""])),
          ...commonFields,
        };
      case "By the Vault":
        return {
          per_vault_rate: "",
          ...commonFields,
        };
      case "Storage Locations Occupied":
        return {
          per_rack_location_rate: "",
          per_oversized_rack_location_rate: "",
          per_floor_location_rate: "",
          square_foot_item_dimensions_rate: "",
          cubic_foot_item_dimensions_rate: "",
          per_vault_rate: "",
          per_trailer_rate: "",
          ...commonFields,
        };
      case "Flat Rate/As Quoted":
        return {
          flat_rate: "",
          ...commonFields,
        };
      case "Square Foot by Item Dimensions":
        return {
          ...Object.fromEntries(squareFootageRateKeys.map(key => [key, ""])),
          ...commonFields,
        };
      case "Square Foot by Location Dimensions":
        return {
          square_foot_location_dimensions_rate: "",
          ...commonFields,
        };
      case "Cubic Foot by Item Dimensions":
        return {
          cubic_foot_item_dimensions_rate: "",
          ...commonFields,
        };
      case "Cubic Foot by Location Dimensions":
        return {
          cubic_foot_location_dimensions_rate: "",
          ...commonFields,
        };
      case "Time and Materials (Blended Rate)":
        return {
          time_and_materials_rate: "",
          ...commonFields,
        };
      case "Time and Materials (Itemized Rates)":
        return {
          installer_rates: "",
          warehouse_labor_rates: "",
          fork_lift_rates: "",
          ...commonFields,
        };
      case "Flat Rate/As Quoted (Internal Work Orders)":
        return {
          // No rate fields - rates are set per transaction
          ...commonFields,
        };
      default:
        return commonFields;
    }
  };

  const getFieldOrderForSingleBillingType = billingType => {
    switch (billingType) {
      case "By the Pallet":
        return [
          ...palletRateKeys,
          "transaction_processing_rate",
          "transaction_rush_rate",
        ];
      case "Hundredweight":
        return [
          "hundredweight_rate",
          "minimum_charge_rate",
          "transaction_processing_rate",
          "transaction_rush_rate",
        ];
      case "By the Pallet (Warehouse-Built)":
        return [
          ...warehouseBuiltPalletRateKeys,
          "transaction_processing_rate",
          "transaction_rush_rate",
        ];
      case "Square Foot by Item Dimensions":
        return [
          ...squareFootageRateKeys,
          "transaction_processing_rate",
          "transaction_rush_rate",
        ];
      case "Square Foot by Location Dimensions":
        return [
          "square_foot_location_dimensions_rate",
          "transaction_processing_rate",
          "transaction_rush_rate",
        ];
      case "Cubic Foot by Item Dimensions":
        return [
          "cubic_foot_item_dimensions_rate",
          "transaction_processing_rate",
          "transaction_rush_rate",
        ];
      case "Cubic Foot by Location Dimensions":
        return [
          "cubic_foot_location_dimensions_rate",
          "transaction_processing_rate",
          "transaction_rush_rate",
        ];
      case "By the Unit/Item":
        return [...unitRateKeys, "transaction_processing_rate", "transaction_rush_rate"];
      case "By the Vault":
        return ["per_vault_rate", "transaction_processing_rate", "transaction_rush_rate"];
      case "Storage Locations Occupied":
        return [
          "per_rack_location_rate",
          "per_oversized_rack_location_rate",
          "per_floor_location_rate",
          "per_vault_rate",
          "per_trailer_rate",
          "transaction_processing_rate",
          "transaction_rush_rate",
        ];
      case "Flat Rate/As Quoted":
        return ["flat_rate", "transaction_processing_rate", "transaction_rush_rate"];
      case "Time and Materials (Blended Rate)":
        return ["time_and_materials_rate", "transaction_processing_rate", "transaction_rush_rate"];
      case "Time and Materials (Itemized Rates)":
        return [
          "installer_rates",
          "warehouse_labor_rates",
          "fork_lift_rates",
          "transaction_processing_rate",
          "transaction_rush_rate",
        ];
      case "Flat Rate/As Quoted (Internal Work Orders)":
        return [
          // No rate fields for this type
          "transaction_processing_rate",
          "transaction_rush_rate",
        ];
      default:
        return null;
    }
  };

  const joinPaths = (...parts) => parts.filter(Boolean).join(".");

  const billingTypes = get(values, joinPaths(entityPath, "billingTypes")) || {};
  const billingRates = get(values, joinPaths(entityPath, "billingRates")) || {};

  useEffect(() => {
    const initialShowCommon = {};
    ["inbound", "outbound"].forEach(key => {
      const billingTypeValue = get(values, joinPaths(entityPath, `billingTypes.${key}`));
      const transactionFields = getTransactionFieldsForCategory(key, billingTypeValue);
      const hasNonZeroRate = transactionFields.some(field => {
        const value = get(billingRates, `${key}.${field}`);
        return value && parseFloat(value) !== 0;
      });
      if (hasNonZeroRate) {
        initialShowCommon[key] = true;
      }
    });
    setShowCommon(prev => ({ ...prev, ...initialShowCommon }));
  }, []);

  // Display categories in this exact order
  const allBillingTypeKeys = ["inbound", "outbound", "storage", "internal"];
  const sortedBillingTypeKeys = focusSection
    ? allBillingTypeKeys.filter(key => key === focusSection)
    : allBillingTypeKeys;

  const getValue = key => {
    const value = billingTypes[key];
    return value || "";
  };

  const getDisplayValue = key => {
    const value = billingTypes[key];
    if (!value) return "";
    return getBillingTypesArray(value);
  };

  const handleChange = key => event => {
    setFieldValue(joinPaths(entityPath, `billingTypes.${key}`), event.target.value);
  };

  const handleClose = () => setOpen(false);

  const handleAddBillingType = (name, billingType) => {
    const formattedKey = snakeCase(name);
    setFieldValue(joinPaths(entityPath, `billingTypes.${formattedKey}`), billingType);
  };

  const handleBillingTypeSelect = (key, billingTypeData) => {
    // Handle both single values (legacy), arrays, and new object structure with comments
    if (typeof billingTypeData === "object" && billingTypeData.types) {
      // New structure with comments: { types: [...], comments: {...} }
      setFieldValue(joinPaths(entityPath, `billingTypes.${key}`), billingTypeData);
    } else {
      // Legacy: single value or array
      setFieldValue(joinPaths(entityPath, `billingTypes.${key}`), billingTypeData);
    }
    setBillingTypeModalOpen(null);
  };

  const formatBillingTypeDisplay = (value, key) => {
    if (!value) return "";

    // Determine if we should show "most common" label
    const showMostCommon = (index, totalCount) => {
      // Never show for internal work orders
      if (key === "internal") return false;
      // Only show if there are multiple types and this is the first one
      return index === 0 && totalCount > 1;
    };

    // Handle new structure with types and comments
    if (typeof value === "object" && value.types) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
          {value.types.map((type, index) => (
            <Box key={type} sx={{ textAlign: "left" }}>
              <Typography component="span" color="text.primary" sx={{ fontWeight: 400 }}>
                {getBillingLabel(type)}
              </Typography>
              {showMostCommon(index, value.types.length) && (
                <Typography component="span" color="primary.main" sx={{ fontWeight: 500, ml: 1 }}>
                  (most common)
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      );
    }
    if (Array.isArray(value)) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
          {value.map((type, index) => (
            <Box key={type} sx={{ textAlign: "left" }}>
              <Typography component="span" color="text.primary" sx={{ fontWeight: 400 }}>
                {getBillingLabel(type)}
              </Typography>
              {showMostCommon(index, value.length) && (
                <Typography component="span" color="primary.main" sx={{ fontWeight: 500, ml: 1 }}>
                  (most common)
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      );
    }
    return getBillingLabel(value);
  };

  const renderSeparateBillingTypeRates = (billingTypes, key) => {
    if (!Array.isArray(billingTypes) || billingTypes.length <= 1) {
      return null;
    }

    return billingTypes.map((billingType, index) => {
      const rateFields = getRateFieldsForBillingType(billingType, key, false); // Exclude transaction fields
      const filteredRateFields = Object.fromEntries(
        Object.entries(rateFields).filter(([rateKey, rateValue]) => {
          const terms = search
            .toLowerCase()
            .split(",")
            .map(t => t.trim())
            .filter(Boolean);
          const label = (getBillingLabel(rateKey, rateValue) || "").toLowerCase();
          return terms.length === 0 || terms.some(t => label.includes(t));
        })
      );
      const displayRateFields = search.trim() ? filteredRateFields : rateFields;

      return (
        <Box key={`${billingType}-${index}`} sx={{ mb: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              mb: 1,
              px: 2,
              py: 1,
              backgroundColor: "grey.50",
              borderRadius: 1,
            }}
          >
            {getBillingLabel(billingType)}
          </Typography>
          <Table size="small" sx={{ width: "100%" }}>
            {billingType === "Flat Rate/As Quoted (Internal Work Orders)" ? (
              renderFlatRateIWOPlaceholder()
            ) : (
              <EditableBillingRates
                title="Billing Rates"
                displayHeader={false}
                baseName={joinPaths(entityPath, `billingRates.${key}`)}
                fieldsObject={displayRateFields}
                readOnly={readOnly}
                fieldOrder={getFieldOrderForSingleBillingType(billingType)}
              />
            )}
          </Table>
        </Box>
      );
    });
  };

  const renderFlatRateIWOPlaceholder = () => {
    return (
      <TableBody>
        <TableRow>
          <TableCell sx={styles.cell}>
            <Box display="flex" alignItems="center">
              <KeyboardReturnIcon
                htmlColor="#d5d5d5"
                sx={{
                  mr: 1,
                  transform: "rotate(180deg) scaleY(-1)",
                }}
              />
              <Typography fontSize={16}>Flat Rate</Typography>
            </Box>
          </TableCell>
          <TableCell sx={styles.cell} align="right">
            <Box display="flex" justifyContent="flex-end">
              <Typography fontSize={16} color="text.secondary" fontStyle="italic">
                (Set for Each Transaction)
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  };

  const getPriorityBasedFieldOrder = billingTypes => {
    if (!billingTypes) return null;

    if (!Array.isArray(billingTypes)) {
      return getFieldOrderForSingleBillingType(billingTypes);
    }

    const allFields = [];
    billingTypes.forEach(billingType => {
      const fields = getFieldOrderForSingleBillingType(billingType);
      if (fields) {
        allFields.push(...fields);
      }
    });

    return [...new Set(allFields)];
  };

  const showEditPalletTypes = key => {
    const val = getDisplayValue(key);
    if (!val) return false;
    if (Array.isArray(val)) return val.includes("By the Pallet");
    if (typeof val === "string") return val === "By the Pallet";
    return false;
  };

  const showEditUnitTypes = key => {
    const val = getDisplayValue(key);
    if (!val) return false;
    if (Array.isArray(val)) return val.includes("By the Unit/Item");
    if (typeof val === "string") return val === "By the Unit/Item";
    return false;
  };

  const showEditSquareFootageTypes = key => {
    return ["inbound", "outbound", "storage"].includes(key);
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap={2} flexGrow={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          {dropdownComponent}
          <SearchField value={search} onChange={setSearch} placeholder="Search" />
        </Box>
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableBody>
              {sortedBillingTypeKeys.map(key => {
                // Always exclude transaction fields from billing type sections
                const allRateFields = getRateFieldsForBillingType(getDisplayValue(key), key, false);
                const visibleRateFields = allRateFields;

                const filteredRateFields = Object.fromEntries(
                  Object.entries(visibleRateFields).filter(([rateKey, rateValue]) =>
                    (getBillingLabel(rateKey, rateValue) || "")
                      .toLowerCase()
                      .includes(search.toLowerCase())
                  )
                );

                const displayRateFields = search.trim() ? filteredRateFields : visibleRateFields;

                if (search.trim() && Object.keys(displayRateFields).length === 0) {
                  return null;
                }

                return (
                  <React.Fragment key={key}>
                    <TableRow>
                      <TableCell sx={styles.cell} width="40%">
                        <Typography fontWeight={500}>{getBillingLabel(key)}</Typography>
                      </TableCell>
                      <TableCell sx={styles.cell} align="right">
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                          {!getDisplayValue(key) && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ minWidth: 100 }}
                            >
                              Not Configured Yet
                            </Typography>
                          )}
                          <Button
                            variant="outlined"
                            size="medium"
                            onClick={() => setBillingTypeModalOpen(key)}
                            sx={{ minWidth: 220, justifyContent: "center" }}
                          >
                            {formatBillingTypeDisplay(getValue(key), key) ||
                              "Select Billing Type(s)"}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ p: 0 }}>
                        {getDisplayValue(key) && (
                          <Box sx={{ p: 2 }}>
                            {Array.isArray(getDisplayValue(key)) &&
                            getDisplayValue(key).length > 1 ? (
                              // Show separate sections for each billing type when multiple are selected
                              renderSeparateBillingTypeRates(getDisplayValue(key), key)
                            ) : getDisplayValue(key) ===
                              "Flat Rate/As Quoted (Internal Work Orders)" ? (
                              // Special case: Flat Rate for Internal Work Orders shows placeholder text
                              <Table size="small" stickyHeader sx={{ width: "100%" }}>
                                {renderFlatRateIWOPlaceholder()}
                              </Table>
                            ) : (
                              // Show merged view for single billing type or legacy format
                              <Table size="small" stickyHeader sx={{ width: "100%" }}>
                                <EditableBillingRates
                                  title="Billing Rates"
                                  displayHeader={false}
                                  baseName={joinPaths(entityPath, `billingRates.${key}`)}
                                  fieldsObject={displayRateFields}
                                  readOnly={readOnly}
                                  fieldOrder={getPriorityBasedFieldOrder(getDisplayValue(key))}
                                />
                              </Table>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                    {showCommon[key] && getDisplayValue(key) && key !== "storage" && (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ p: 0 }}>
                          <Box sx={{ p: 2 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 500,
                                mb: 1,
                                px: 2,
                                py: 1,
                                backgroundColor: "grey.50",
                                borderRadius: 1,
                              }}
                            >
                              Handling Charges
                            </Typography>
                            <Table size="small" sx={{ width: "100%" }}>
                              <EditableBillingRates
                                title="Transaction Rates"
                                displayHeader={false}
                                baseName={joinPaths(entityPath, `billingRates.${key}`)}
                                fieldsObject={Object.fromEntries(
                                  getTransactionFieldsForCategory(key, getValue(key)).map(field => [field, ""])
                                )}
                                readOnly={readOnly}
                                fieldOrder={getTransactionFieldsForCategory(key, getValue(key))}
                              />
                            </Table>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    {!readOnly &&
                      getDisplayValue(key) &&
                      ((!showCommon[key] && key !== "storage") ||
                        showEditPalletTypes(key) ||
                        showEditUnitTypes(key) ||
                        showEditSquareFootageTypes(key)) && (
                        <TableRow>
                          <TableCell colSpan={2} align="left">
                            <Box display="flex" justifyContent="space-between" width="100%">
                              {!showCommon[key] && key !== "storage" ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setShowCommon(prev => ({ ...prev, [key]: true }))}
                                >
                                  Add Separate Transaction Rates
                                </Button>
                              ) : (
                                <Box />
                              )}
                              {showEditPalletTypes(key) && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setProjectPalletTypesOpen(true)}
                                >
                                  Edit {user?.company?.settings?.filter_field2} Pallet Types
                                </Button>
                              )}
                              {showEditUnitTypes(key) && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setProjectUnitTypesOpen(true)}
                                >
                                  Edit {user?.company?.settings?.filter_field2} Unit Types
                                </Button>
                              )}
                              {showEditSquareFootageTypes(key) && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => setProjectSquareFootageTypesOpen(true)}
                                >
                                  Edit {user?.company?.settings?.filter_field2} Square Footage Types
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {open && (
        <AddFieldModal
          onClose={handleClose}
          onAdd={handleAddBillingType}
          options={BILLING_TYPE_OPTIONS}
        />
      )}
      {billingTypeModalOpen && (
        <BillingTypeSelectionModal
          open={true}
          onClose={() => setBillingTypeModalOpen(null)}
          currentValue={getValue(billingTypeModalOpen)}
          category={billingTypeModalOpen}
          onSelect={billingType => handleBillingTypeSelect(billingTypeModalOpen, billingType)}
          enabledBillingTypes={user?.company?.enabledBillingTypes || []}
          categoryDisplayText={
            billingTypeModalOpen === "inbound"
              ? "Inbound Services"
              : billingTypeModalOpen === "outbound"
              ? "Outbound Services"
              : billingTypeModalOpen === "storage"
              ? "Storage Services"
              : billingTypeModalOpen === "internal_work_orders"
              ? "Internal Work Orders"
              : null
          }
          inboundBillingTypes={billingTypeModalOpen === "storage" ? getValue("inbound") : null}
        />
      )}
      {projectPalletTypesOpen && (
        <ProjectPalletTypesDialog
          open={projectPalletTypesOpen}
          onClose={() => setProjectPalletTypesOpen(false)}
        />
      )}
      {projectUnitTypesOpen && (
        <ProjectUnitTypesDialog
          open={projectUnitTypesOpen}
          onClose={() => setProjectUnitTypesOpen(false)}
        />
      )}
      {projectSquareFootageTypesOpen && (
        <ProjectSquareFootageTypesDialog
          open={projectSquareFootageTypesOpen}
          onClose={() => setProjectSquareFootageTypesOpen(false)}
        />
      )}
    </>
  );
}
